import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ColorPicker from './ColorPicker';
import ConfiguratorAdditionalItems from './ConfiguratorAdditionalItems';
import { useTranslation } from '../context/TranslationContext';
import {
  DAKKAPEL_ELEMENT_TYPES,
  DAKKAPEL_MATERIALS,
  DAKKAPEL_SUN_PROTECTION,
  DAKKAPEL_ROOF_STRUCTURES,
  DAKKAPEL_ROOF_CONNECTIONS,
  DAKKAPEL_MIN_WIDTH_MM,
  DAKKAPEL_MAX_WIDTH_MM,
  DAKKAPEL_MIN_HEIGHT_MM,
  DAKKAPEL_MAX_HEIGHT_MM,
  DAKKAPEL_MIN_ELEMENT_WIDTH_MM,
  DAKKAPEL_MAX_ELEMENTS,
  getDakkapelElementMaxWidthMm,
  clampDakkapelHeightMm,
  getMaxDakkapelElements,
  createDakkapelElements,
  updateElementWidth,
  updateElementType,
  applyWidthDrivenLayout,
  stepDakkapelDimensionMm,
  stepDakkapelElementWidthMm,
  isDakkapelOpeningElementType,
  normalizeDakkapelElementType,
} from '../data/dakkapelOptions';

/** Falls back to the cream the drawing already defaults to when a colour is unset. */
const DEFAULT_COLOR_ID = 'ral-9001';

/** Collapsible topical section, matching the card + chevron pattern used across the app. */
const Section = ({ title, description, open, onToggle, children }) => (
  <Card>
    <CardHeader className="pb-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-3 text-left rounded-md -m-1 p-1 hover:bg-gray-50 transition-colors"
      >
        <div className="min-w-0">
          <CardTitle className="text-base">{title}</CardTitle>
          {description ? (
            <CardDescription className="text-xs mt-1">{description}</CardDescription>
          ) : null}
        </div>
        <ChevronDown
          className={`w-5 h-5 shrink-0 mt-0.5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
    </CardHeader>
    {open ? <CardContent className="space-y-5">{children}</CardContent> : null}
  </Card>
);

const OptionGrid = ({ options, value, onChange, columns = 2 }) => (
  <div className={`grid gap-2 ${columns === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
    {options.map((option) => {
      const selected = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`rounded-lg border-2 px-3 py-2.5 text-left text-sm transition-all hover:border-orange-400 hover:bg-orange-50 ${
            selected
              ? 'border-orange-500 bg-orange-50 font-medium text-orange-900 shadow-sm'
              : 'border-gray-200 bg-white text-gray-700'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

/** Compact numeric picker for counts, using the dark selected state the app uses for parts. */
const CountPills = ({ values, value, onChange, renderLabel, isDisabled }) => (
  <div className="flex flex-wrap gap-1.5">
    {values.map((n) => {
      const disabled = isDisabled?.(n);
      const selected = value === n;
      return (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(n)}
          className={`flex h-9 min-w-[2rem] items-center justify-center rounded border px-2.5 text-sm font-semibold transition-all ${
            disabled
              ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
              : selected
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-gray-100 text-gray-700 hover:border-gray-400'
          }`}
        >
          {renderLabel ? renderLabel(n) : n}
        </button>
      );
    })}
  </div>
);

/** Overall width/height stepper: − value mm + (50 mm steps). */
const MmStepper = ({ label, valueMm, minMm, maxMm, onChangeMm }) => {
  const displayMm = Math.round(Number(valueMm) || 0);
  const atMin = displayMm <= minMm;
  const atMax = displayMm >= maxMm;
  return (
    <div>
      <Label className="mb-2 block text-sm font-bold">{label}</Label>
      <div className="flex items-center justify-between gap-3 bg-gray-200 px-4 py-2">
        <button
          type="button"
          aria-label={`${label} −`}
          disabled={atMin}
          onClick={() => onChangeMm(stepDakkapelDimensionMm(displayMm, -1, minMm, maxMm))}
          className="text-lg font-bold leading-none text-gray-700 disabled:opacity-30 px-1"
        >
          −
        </button>
        <span className="flex-1 text-center text-sm font-semibold tabular-nums">{displayMm} mm</span>
        <button
          type="button"
          aria-label={`${label} +`}
          disabled={atMax}
          onClick={() => onChangeMm(stepDakkapelDimensionMm(displayMm, 1, minMm, maxMm))}
          className="text-lg font-bold leading-none text-gray-700 disabled:opacity-30 px-1"
        >
          +
        </button>
      </div>
    </div>
  );
};

const ElementWidthStepper = ({ label, valueMm, maxMm, onChangeMm }) => {
  const displayMm = Math.round(Number(valueMm) || 0);
  const atMin = valueMm <= DAKKAPEL_MIN_ELEMENT_WIDTH_MM;
  const atMax = maxMm != null && valueMm >= maxMm;
  return (
    <div className="flex items-center gap-2">
      <Label className="text-xs text-gray-500 shrink-0">{label}</Label>
      <div className="flex items-center gap-2 bg-gray-200 px-2 py-1">
        <button
          type="button"
          aria-label={`${label} −`}
          disabled={atMin}
          onClick={() => onChangeMm(stepDakkapelElementWidthMm(valueMm, -1, maxMm ?? undefined))}
          className="text-base font-bold leading-none text-gray-700 disabled:opacity-30 px-1"
        >
          −
        </button>
        <span className="min-w-[5rem] text-center text-sm font-semibold tabular-nums">{displayMm} mm</span>
        <button
          type="button"
          aria-label={`${label} +`}
          disabled={atMax}
          onClick={() => onChangeMm(stepDakkapelElementWidthMm(valueMm, 1, maxMm ?? undefined))}
          className="text-base font-bold leading-none text-gray-700 disabled:opacity-30 px-1"
        >
          +
        </button>
      </div>
    </div>
  );
};

/**
 * Label above a dropdown — the shape every entry in the options section takes. These are
 * one-of-a-few choices that never need to be visible at a glance, so a single control
 * reads better than a row of buttons and keeps each entry to one line whatever the number
 * of choices behind it.
 */
const SelectField = ({ label, value, options, onChange }) => (
  <div className="min-w-0">
    <Label className="mb-2 block">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/** Two fields to a row, dropping to one when the panel is narrow. */
const FieldRow = ({ children }) => <div className="grid gap-4 sm:grid-cols-2">{children}</div>;

/** Same title + hint + RAL search picker the window and door panels use for colours. */
const ColorField = ({ title, hint, value, fallback, onChange }) => (
  <div>
    <p className="text-sm font-semibold leading-none tracking-tight text-gray-900">{title}</p>
    <p className="mt-1 mb-2 text-xs text-muted-foreground">{hint}</p>
    <ColorPicker selectedColor={value || fallback} onColorChange={onChange} showSearch />
  </div>
);

const DakkapelOptionsPanel = ({
  config,
  onConfigChange,
  onConfigPatch,
  glazingTypes = [],
  additionalItems,
  onAdditionalItemsChange,
}) => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState({
    dimensions: true,
    layout: true,
    material: false,
    colors: false,
    options: false,
  });

  const toggleSection = (id) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const patch = (partial) => {
    if (onConfigPatch) onConfigPatch(partial);
    else Object.entries(partial).forEach(([k, v]) => onConfigChange(k, v));
  };

  const commitWidthMm = (widthMm) => {
    patch(applyWidthDrivenLayout(widthMm, config.elements));
  };

  const commitHeightMm = (heightMm) => {
    patch({ height: clampDakkapelHeightMm(heightMm) });
  };

  const maxElements = getMaxDakkapelElements(config.width);
  const elementCount = config.elements?.length || 2;

  const setElementCount = (n) => {
    const count = Math.min(maxElements, Math.max(2, Number(n) || 2));
    patch({
      elementCountManual: true,
      elements: createDakkapelElements(count, config.width, config.elements),
    });
  };

  const setElementType = (index, type) => {
    patch({
      elements: updateElementType(config.elements || [], index, type, config.width),
    });
  };

  const setElementWidthMm = (index, mmValue) => {
    const parsed = Math.round(Number(mmValue));
    if (Number.isNaN(parsed)) return;
    const elements = updateElementWidth(config.elements || [], index, parsed, config.width);
    patch({ elements });
  };

  const tiltTurnCount = (config.elements || []).filter((el) =>
    isDakkapelOpeningElementType(el.type),
  ).length;
  const maxScreens = Math.max(tiltTurnCount, 1);
  const colorHint = t('options.selectFromStandardColors');
  const yesNoOptions = [
    { value: 'no', label: t('dakkapel.no') },
    { value: 'yes', label: t('dakkapel.yes') },
  ];
  const toOptions = (list) => list.map((item) => ({ value: item.id, label: t(item.labelKey) }));

  // Counts read as an answer to the question rather than a bare number, so a closed
  // dropdown still says what was chosen: "Nee", "Ja, 2×".
  const screenCountLabel = (n) => (n === 0 ? t('dakkapel.no') : t('dakkapel.yesCount', { n }));
  const grilleCountLabel = (n) =>
    n === 0
      ? t('dakkapel.no')
      : t(n === 1 ? 'dakkapel.yesGrilleCount' : 'dakkapel.yesGrilleCountPlural', { n });
  /**
   * Select values are strings, so counts go in and come back out as text. The list always
   * stretches to cover `current`, so a saved count that outgrew its cap (fewer opening
   * sashes than screens, say) still shows up instead of leaving the field blank.
   */
  const countOptions = (max, current, renderLabel) =>
    Array.from({ length: Math.max(0, max, current) + 1 }, (_, n) => ({
      value: String(n),
      label: renderLabel(n),
    }));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl xl:text-2xl font-bold text-gray-900">{t('dakkapel.panelTitle')}</h2>
        <p className="text-sm text-gray-500 mt-1">{t('dakkapel.panelSubtitle')}</p>
      </div>

      <Section
        title={t('dakkapel.sectionDimensions')}
        description={t('dakkapel.sectionDimensionsDesc')}
        open={openSections.dimensions}
        onToggle={() => toggleSection('dimensions')}
      >
        <div className="space-y-4">
          <MmStepper
            label={t('dakkapel.widthMm')}
            valueMm={config.width}
            minMm={DAKKAPEL_MIN_WIDTH_MM}
            maxMm={DAKKAPEL_MAX_WIDTH_MM}
            onChangeMm={commitWidthMm}
          />
          <MmStepper
            label={t('dakkapel.heightMm')}
            valueMm={config.height}
            minMm={DAKKAPEL_MIN_HEIGHT_MM}
            maxMm={DAKKAPEL_MAX_HEIGHT_MM}
            onChangeMm={commitHeightMm}
          />
        </div>
      </Section>

      <Section
        title={t('dakkapel.sectionLayout')}
        description={t('dakkapel.sectionLayoutDesc')}
        open={openSections.layout}
        onToggle={() => toggleSection('layout')}
      >
        <div>
          <Label className="mb-2 block">{t('dakkapel.elementCount')}</Label>
          <CountPills
            values={Array.from({ length: DAKKAPEL_MAX_ELEMENTS }, (_, i) => i + 1)}
            value={elementCount}
            onChange={setElementCount}
            isDisabled={(n) => n < 2 || n > maxElements}
          />
        </div>

        <div className="space-y-3">
          {(config.elements || []).map((el, index) => (
            <div key={el.id || index} className="rounded-lg border border-gray-200 p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-800">
                  {t('dakkapel.elementN', { n: index + 1 })}
                </p>
                <ElementWidthStepper
                  label={t('dakkapel.elementWidthMm')}
                  valueMm={el.widthMm}
                  maxMm={getDakkapelElementMaxWidthMm(index, elementCount, config.width)}
                  onChangeMm={(mm) => setElementWidthMm(index, mm)}
                />
              </div>
              <OptionGrid
                options={toOptions(DAKKAPEL_ELEMENT_TYPES)}
                value={normalizeDakkapelElementType(el.type)}
                onChange={(type) => setElementType(index, type)}
              />
            </div>
          ))}
        </div>

        {glazingTypes.length > 0 ? (
          <div>
            <Label className="mb-2 block">{t('options.glazingType')}</Label>
            <OptionGrid
              options={glazingTypes.map((glazing) => ({
                value: glazing.id,
                label: glazing.name,
              }))}
              value={config.glazingType || 'double'}
              onChange={(v) => onConfigChange('glazingType', v)}
              columns={1}
            />
          </div>
        ) : null}
      </Section>

      <Section
        title={t('dakkapel.sectionMaterial')}
        description={t('dakkapel.sectionMaterialDesc')}
        open={openSections.material}
        onToggle={() => toggleSection('material')}
      >
        <div>
          <Label className="mb-2 block">{t('dakkapel.material')}</Label>
          <OptionGrid
            options={toOptions(DAKKAPEL_MATERIALS)}
            value={config.material}
            onChange={(v) => onConfigChange('material', v)}
            columns={1}
          />
        </div>
      </Section>

      <Section
        title={t('dakkapel.sectionColors')}
        description={t('dakkapel.sectionColorsDesc')}
        open={openSections.colors}
        onToggle={() => toggleSection('colors')}
      >
        <ColorField
          title={t('dakkapel.claddingColor')}
          hint={colorHint}
          value={config.claddingColor}
          fallback={DEFAULT_COLOR_ID}
          onChange={(id) => onConfigChange('claddingColor', id)}
        />
        <ColorField
          title={t('dakkapel.frameColor')}
          hint={colorHint}
          value={config.frameColor}
          fallback={DEFAULT_COLOR_ID}
          onChange={(id) => onConfigChange('frameColor', id)}
        />
        <ColorField
          title={t('dakkapel.fasciaColor')}
          hint={colorHint}
          value={config.fasciaColor}
          fallback={DEFAULT_COLOR_ID}
          onChange={(id) => onConfigChange('fasciaColor', id)}
        />
        <ColorField
          title={t('dakkapel.tiltTurnColor')}
          hint={colorHint}
          value={config.tiltTurnColor}
          fallback={DEFAULT_COLOR_ID}
          onChange={(id) => onConfigChange('tiltTurnColor', id)}
        />
      </Section>

      <Section
        title={t('dakkapel.sectionOptions')}
        description={t('dakkapel.sectionOptionsDesc')}
        open={openSections.options}
        onToggle={() => toggleSection('options')}
      >
        <FieldRow>
          <SelectField
            label={t('dakkapel.sunProtection')}
            value={config.sunProtection || 'none'}
            options={toOptions(DAKKAPEL_SUN_PROTECTION)}
            onChange={(v) => onConfigChange('sunProtection', v)}
          />
          <SelectField
            label={t('dakkapel.insectScreens')}
            value={String(config.insectScreens || 0)}
            options={countOptions(maxScreens, config.insectScreens || 0, screenCountLabel)}
            onChange={(v) => onConfigChange('insectScreens', Number(v))}
          />
        </FieldRow>
        <FieldRow>
          <SelectField
            label={t('dakkapel.permitApplication')}
            value={config.permitApplication ? 'yes' : 'no'}
            options={yesNoOptions}
            onChange={(v) => onConfigChange('permitApplication', v === 'yes')}
          />
          <SelectField
            label={t('dakkapel.debrisRemoval')}
            value={config.debrisRemoval ? 'yes' : 'no'}
            options={yesNoOptions}
            onChange={(v) => onConfigChange('debrisRemoval', v === 'yes')}
          />
        </FieldRow>
        <FieldRow>
          <SelectField
            label={t('dakkapel.roofStructure')}
            value={config.roofStructure || 'purlins'}
            options={toOptions(DAKKAPEL_ROOF_STRUCTURES)}
            onChange={(v) => onConfigChange('roofStructure', v)}
          />
          <SelectField
            label={t('dakkapel.roofConnection')}
            value={config.roofConnection || 'ubiflex-black'}
            options={toOptions(DAKKAPEL_ROOF_CONNECTIONS)}
            onChange={(v) => onConfigChange('roofConnection', v)}
          />
        </FieldRow>
        <FieldRow>
          <SelectField
            label={t('dakkapel.airco')}
            value={config.airco ? 'yes' : 'no'}
            options={yesNoOptions}
            onChange={(v) => onConfigChange('airco', v === 'yes')}
          />
          <SelectField
            label={t('dakkapel.ventilationGrilles')}
            value={String(config.ventilationGrilles || 0)}
            options={countOptions(
              Math.max(elementCount, 1),
              config.ventilationGrilles || 0,
              grilleCountLabel
            )}
            onChange={(v) => onConfigChange('ventilationGrilles', Number(v))}
          />
        </FieldRow>
      </Section>

      {onAdditionalItemsChange ? (
        <ConfiguratorAdditionalItems
          items={additionalItems}
          onItemsChange={onAdditionalItemsChange}
          config={config}
          onConfigChange={onConfigChange}
        />
      ) : null}
    </div>
  );
};

export default DakkapelOptionsPanel;
